import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, CheckCircle, Clock, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import AppLogoFull from '../components/AppLogoFull';
import PackageFormShell from '../components/package-form/PackageFormShell';
import { STEP_IDS, createEmptyForm, usePackageForm } from '../components/package-form/usePackageForm';
import { buildQrSubmitPayload } from '../components/package-form/payload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Écran d'état (session invalide, envoi confirmé) sur le même fond que le formulaire. */
const StatusScreen = ({ tone, icon: Icon, title, children }) => (
  <div className="qr-page">
    <div className="qr-status">
      <span className="qr-status-mark" style={{ color: tone }} aria-hidden="true">
        <Icon size={22} />
      </span>
      <h2>{title}</h2>
      {children}
    </div>
  </div>
);

const QRClientForm = () => {
  const { qr_token } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isFr = language !== 'en';

  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [error, setError] = useState('');
  const [merchandiseCategories, setMerchandiseCategories] = useState([]);

  // Le client ne saisit pas de montant : variante « qr » du formulaire partagé.
  const formApi = usePackageForm({ variant: 'qr', initialForm: createEmptyForm() });
  const { form } = formApi;

  const allCharterAccepted = form.charter.inspectionConsent
    && form.charter.termsAccepted
    && form.charter.noProhibitedItems;

  useEffect(() => {
    const fetchSessionInfo = async () => {
      try {
        const response = await axios.get(`${API}/qr-sessions/${qr_token}`);
        setSessionInfo(response.data);
        setMerchandiseCategories(response.data.merchandise_categories || []);
        formApi.setField('transport_mode', response.data.default_transport_mode || 'aerien');
        // Le client ne choisit pas son point de départ : il vient du dépôt.
        formApi.setField('origin_country', response.data.origin_country || '');
      } catch (err) {
        if (err.response?.status === 404) {
          setError(t('qrSessionNotFound'));
        } else if (err.response?.status === 410) {
          setError(err.response.data.detail || t('qrSessionExpired'));
        } else {
          setError(t('qrLoadError'));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSessionInfo();
  }, [qr_token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!STEP_IDS.every((id) => formApi.validateStep(id))) return;

    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/qr-sessions/${qr_token}/submit`, buildQrSubmitPayload(form));

      // Chaque photo est rattachée au colis qui l'a produite.
      const uploads = form.items.flatMap((item, index) => (item.photos || []).map(({ file }) => {
        const fd = new FormData();
        fd.append('file', file);
        return axios.post(`${API}/qr-sessions/${qr_token}/upload-photo?package_index=${index}`, fd);
      }));
      if (uploads.length > 0) {
        const results = await Promise.allSettled(uploads);
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) console.warn(`${failed}/${uploads.length} photo uploads failed`);
      }

      setSubmittedCount(form.items.length);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('qrSubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="qr-page">
        <div className="qr-status">
          <Package className="h-9 w-9 mx-auto text-accent animate-spin mb-4" />
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !sessionInfo) {
    return (
      <StatusScreen tone="#b42318" icon={AlertCircle} title={t('invalidSession')}>
        <p>{error}</p>
        <Button className="mt-4" onClick={() => navigate('/')}>{t('backToHome')}</Button>
      </StatusScreen>
    );
  }

  if (submitted) {
    return (
      <StatusScreen tone="hsl(152 45% 34%)" icon={CheckCircle} title={t('infoSent')}>
        {submittedCount > 1 && (
          <p><strong>{submittedCount}</strong> {isFr ? 'colis déclarés.' : 'packages declared.'}</p>
        )}
        <p>
          {t('infoTransmittedTo')} <strong>{sessionInfo?.partner_name}</strong>.
          {' '}{t('willVerifyAndCreate')}
        </p>
        <p>{t('willReceiveConfirmation')}</p>
      </StatusScreen>
    );
  }

  const expiresAt = sessionInfo?.expires_at
    ? new Date(sessionInfo.expires_at).toLocaleTimeString(isFr ? 'fr-FR' : 'en-GB', { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="qr-page">
      <header className="qr-page-header">
        <div className="qr-page-header-inner">
          <div className="qr-page-brand">
            <AppLogoFull height={26} />
            {expiresAt && (
              <span className="qr-expiry">
                <Clock size={13} aria-hidden="true" />
                {isFr ? `Valable jusqu'à ${expiresAt}` : `Valid until ${expiresAt}`}
              </span>
            )}
          </div>
          <h1>{isFr ? 'Déclarez votre colis' : 'Declare your package'}</h1>
          <p>
            {isFr ? 'Dépôt chez ' : 'Drop-off with '}
            <strong>{sessionInfo?.company_name}</strong>
            {sessionInfo?.partner_name ? ` · ${sessionInfo.partner_name}` : ''}
            {isFr
              ? '. Vos informations sont vérifiées par le partenaire avant enregistrement.'
              : '. Your details are checked by the partner before registration.'}
          </p>
        </div>
      </header>

      <div className="qr-page-body">
        <div className="qr-panel">
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert className="border-red-200 bg-red-500/10">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            <PackageFormShell
              layout="wizard"
              variant="qr"
              api={formApi}
              stepProps={{
                transportModes: sessionInfo?.transport_modes || ['aerien'],
                merchandiseCategories,
                companyName: sessionInfo?.company_name,
              }}
              footer={(
                <Button type="submit" className="w-full sm:w-auto" disabled={submitting || !allCharterAccepted}>
                  {submitting ? t('sending') : t('sendInfo')}
                </Button>
              )}
            />
          </form>
        </div>
      </div>

      <p className="qr-page-foot">{t('submitDisclaimer')}</p>
    </div>
  );
};

export default QRClientForm;
